/**
 * PUBLIC PROFILE PAGE
 *
 * Displays a user's public profile showcasing their nail art collection and activity.
 *
 * Profile Sections:
 * 1. Header
 *    - Banner image
 *    - Avatar with edit button (for own profile)
 *    - Display name and username
 *    - Bio and location
 *    - Social media links (Instagram, YouTube, TikTok, blog, shop)
 *    - Favorite seller button
 *    - Message seller button
 *    - Edit profile button (own profile only)
 *
 * 2. Trust & Reputation
 *    - Verified badges (email verified, member since, active trader, collector)
 *    - Vouches from other users (peer recommendations)
 *    - Can vouch for other users from their profile
 *
 * 3. Collection Statistics
 *    - Total items breakdown (polish, plates, tools, etc.)
 *    - Quick links to filtered collection views
 *    - Respects privacy settings
 *
 * 4. Collection Insights
 *    - Top brands by item count
 *    - Most used items (tracked via timesUsed field)
 *    - Collection health metrics
 *    - Brand categories (indie, boutique, mainstream)
 *    - Configurable visibility via privacy settings
 *
 * 5. Creations Teaser
 *    - Preview of recent nail art (manis, pedis, etc.)
 *    - Link to full creations gallery
 *
 * 6. For Sale (Marketplace)
 *    - Press-ons, jewelry, and other marketplace listings
 *    - Shows price badges
 *
 * 7. Destash Items
 *    - Collection items available for sale/trade
 *    - Links to destash listings
 *
 * 8. Recent Favorites
 *    - Recently favorited collection items
 *    - Shows what the collector loves
 *
 * Privacy System:
 * - Granular privacy controls for each section
 * - User can hide collection, wishlist, stats, insights, etc.
 * - PageVisibilityBanner shows privacy status to owner
 * - Settings accessible from profile edit modal
 *
 * Route: /profile/:username
 *
 * Technical Notes:
 * - Loads user profile from Firestore users collection
 * - Fetches collection items, creations, and vouches
 * - Calculates statistics client-side from collection data
 * - Handles both viewing own profile and others' profiles
 * - Uses brandCategories system for indie/boutique/mainstream classification
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { collection, getDocs, onSnapshot, doc, getDoc, query, where, addDoc, serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import type { CollectionItem } from '../types';
import type { Creation } from '../types/creationTypes';
import type { BrandCategory } from '../types/brandCategories';
import EditProfileModal from '../components/EditProfileModal';
import PolishDetailModal from '../components/polish/PolishDetailModal';
import PlateDetailModal from '../components/plates/PlateDetailModal';
import DecalDetailModal from '../components/decals/DecalDetailModal';
import EmbellishmentDetailModal from '../components/embellishments/EmbellishmentDetailModal';
import ToolDetailModal from '../components/tools/ToolDetailModal';
import BuffingPowderDetailModal from '../components/powders/BuffingPowderDetailModal';
import AcrylicDetailModal from '../components/acrylics/AcrylicDetailModal';
import WishlistContactModal from '../components/WishlistContactModal';
import { PriorityColors, type PriorityKey } from '../constants/categoryColors';
import FavoriteSellerButton from '../components/marketplace/FavoriteSellerButton';
import CreationDetailModal from '../components/creations/CreationDetailModal';
import PageVisibilityBanner from '../components/PageVisibilityBanner';
import { Instagram, Youtube, Globe, MessageSquare, Loader2, Shield, Heart, Calendar, Package, Gift } from 'lucide-react';

interface SocialLinks {
  instagram?: string;
  youtube?: string;
  tiktok?: string;
  facebook?: string;
  blog?: string;
  shop?: string;
}

// Vouch from another user
interface Vouch {
  id: string;
  fromUserId: string;
  fromUsername: string;
  fromDisplayName: string;
  fromAvatarUrl?: string;
  createdAt: Timestamp;
}

// Trust badges earned
interface TrustBadges {
  verifiedEmail: boolean;
  memberSince: Date | null;
  isActiveTrader: boolean;  // 5+ destash items ever
  isCollector: boolean;     // 100+ items
}

interface UserProfile {
  username: string;
  displayName: string;
  bio: string;
  avatarUrl: string;
  bannerUrl: string;
  socialLinks?: SocialLinks;
  isCollectionPublic?: boolean;
  privacy?: {
    isPublic: boolean;
    showCollection: boolean;
    showWishlist: boolean;
    showDestashList: boolean;
    showCreations: boolean;
    showFavorites: boolean;
    showDestashArchive: boolean;
    showRareLE: boolean;
    showSwatched: boolean;
    showProjects: boolean;
    showStats: boolean;
    allowMessages?: boolean;
    allowWishlistContact?: boolean;  // Allow others to contact about wishlist items
    // Granular Collection Insights settings
    showInsightsOverview?: boolean;
    showCollectionValue?: boolean;
    showCollectorBadges?: boolean;
    showCategoryStats?: boolean;
    showPolishBrandTypes?: boolean;
  };
}

interface ProfileStats {
  totalItems: number;
  polishCount: number;
  plateCount: number;
  toolCount: number;
  embellishmentCount: number;
  decalCount: number;
  powderCount: number;
  acrylicCount: number;
  brandCount: number;
  favoriteCount: number;
  // Brand category counts (polish brands only)
  indieCount: number;
  boutiqueCount: number;
  mainstreamCount: number;
  uncategorizedCount: number;
  // Extended stats
  activeCount: number;
  destashedCount: number;
  archivedCount: number;
  listedForSaleCount: number;
  totalValue: number;
  triedCount: number;
  untriedCount: number;
}

// Collector tier for badges
interface CollectorTier {
  tier: string;
  emoji: string;
  label: string;
  color: string;
  bgColor: string;
}

// Per-category statistics
interface CategoryStats {
  itemType: string;
  label: string;
  emoji: string;
  count: number;
  topBrands: { name: string; count: number }[];
  mostUsedItem: CollectionItem | null;
  triedCount: number;
  untriedCount: number;
  percentTried: number;
  favoritesCount: number;
  totalUses: number;
  // Polish-specific (brand type breakdown)
  indieCount?: number;
  boutiqueCount?: number;
  mainstreamCount?: number;
  uncategorizedCount?: number;
  indieBrandCount?: number;
  boutiqueBrandCount?: number;
  mainstreamBrandCount?: number;
  uncategorizedBrandCount?: number;
}

interface CollectionInsights {
  // Overall stats
  topBrands: { name: string; count: number }[];
  mostUsedItem: CollectionItem | null;
  topUsedBrand: { name: string; totalUses: number } | null;
  percentTried: number;
  percentFavorites: number;
  percentDestash: number;
  totalUses: number;
  // Per-category stats
  categoryStats: CategoryStats[];
}

export default function Profile() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to get collector tier based on count and item type
  const getCollectorTier = (count: number, itemType: 'polish' | 'plate' | 'powder' | 'tool' | 'overall'): CollectorTier => {
    // Different thresholds per category
    const thresholds = {
      polish: [25, 100, 500, 1000, 2500],
      plate: [10, 50, 150, 300, 500],
      powder: [10, 30, 75, 150, 250],
      tool: [10, 25, 50, 100, 200],
      overall: [50, 200, 750, 1500, 4000]
    };

    const tiers: CollectorTier[] = [
      { tier: 'budding', emoji: '🌱', label: 'Budding', color: 'text-green-300', bgColor: 'bg-green-500/20 border border-green-500/30' },
      { tier: 'enthusiast', emoji: '💅', label: 'Enthusiast', color: 'text-pink-300', bgColor: 'bg-pink-500/20 border border-pink-500/30' },
      { tier: 'collector', emoji: '✨', label: 'Collector', color: 'text-purple-300', bgColor: 'bg-purple-500/20 border border-purple-500/30' },
      { tier: 'connoisseur', emoji: '💎', label: 'Connoisseur', color: 'text-blue-300', bgColor: 'bg-blue-500/20 border border-blue-500/30' },
      { tier: 'curator', emoji: '👑', label: 'Curator', color: 'text-amber-300', bgColor: 'bg-amber-500/20 border border-amber-500/30' },
      { tier: 'dragon', emoji: '🐉', label: 'Dragon', color: 'text-red-300', bgColor: 'bg-red-500/20 border border-red-500/30' }
    ];

    const t = thresholds[itemType];
    
    if (count === 0) return { tier: 'none', emoji: '', label: '', color: '', bgColor: '' };
    if (count < t[0]) return tiers[0];
    if (count < t[1]) return tiers[1];
    if (count < t[2]) return tiers[2];
    if (count < t[3]) return tiers[3];
    if (count < t[4]) return tiers[4];
    return tiers[5];
  };
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isPreviewingAsVisitor, setIsPreviewingAsVisitor] = useState(searchParams.get('preview') === 'true');
  
  // Collection data
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [insights, setInsights] = useState<CollectionInsights | null>(null);
  
  // Edit profile modal
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Messaging state
  const [isStartingChat, setIsStartingChat] = useState(false);
  
  // Trust & Vouches state
  const [vouches, setVouches] = useState<Vouch[]>([]);
  const [badges, setBadges] = useState<TrustBadges>({
    verifiedEmail: false,
    memberSince: null,
    isActiveTrader: false,
    isCollector: false
  });
  const [hasVouched, setHasVouched] = useState(false);
  const [isVouching, setIsVouching] = useState(false);
  const [showVouchList, setShowVouchList] = useState(false);

  // Creations state
  const [creations, setCreations] = useState<Creation[]>([]);
  

  // Item detail modal state (for viewing items directly on profile)
  const [selectedItem, setSelectedItem] = useState<CollectionItem | null>(null);

  // Wishlist contact modal state (for contacting profile owner about their wishlist items)
  const [wishlistContactItem, setWishlistContactItem] = useState<CollectionItem | null>(null);

  // Creation detail modal state
  const [selectedCreation, setSelectedCreation] = useState<Creation | null>(null);

  // Save item updates to Firebase
  const handleSaveItem = async (itemId: string, updates: Partial<CollectionItem>) => {
    if (!userId) {
      console.error('Cannot save item: userId is null');
      return;
    }
    
    // Only allow saving if viewing own profile (not preview mode)
    if (!isOwnProfile) {
      console.error('Cannot save item: not viewing own profile');
      return;
    }
    
    try {
      const itemRef = doc(db, 'users', userId, 'items', itemId);
      
      // Sanitize updates: Firebase doesn't accept undefined, convert to null
      const sanitizedUpdates: Record<string, any> = {};
      for (const [key, value] of Object.entries(updates)) {
        sanitizedUpdates[key] = value === undefined ? null : value;
      }
      
      await updateDoc(itemRef, sanitizedUpdates);
      
      // Update local state
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      ));
      
      // Update selectedItem if it's the same item
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => prev ? { ...prev, ...updates } : null);
      }
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Load profile by username
  useEffect(() => {
    // Subscription handle for the items collection — kept out of the
    // async loadProfile scope so the useEffect cleanup can tear it down
    // when the component unmounts or `username` changes.
    let unsubItems: (() => void) | null = null;
    const loadProfile = async () => {
      if (!username || username.trim() === '') {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        // Look up user ID from username
        const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));

        if (!usernameDoc.exists()) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const foundUserId = usernameDoc.data()?.userId || usernameDoc.data()?.uid;
        
        // Check if userId exists in the document
        if (!foundUserId) {
          console.error('Username document exists but has no userId or uid:', usernameDoc.data());
          setError('User not found');
          setLoading(false);
          return;
        }

        setUserId(foundUserId);

        // Check if this is the current user's profile
        const currentUser = getCurrentUser();
        const isOwn = currentUser?.uid === foundUserId;
        setIsOwnProfile(isOwn);

        // Load user profile
        const userDoc = await getDoc(doc(db, 'users', foundUserId));
        
        if (!userDoc.exists()) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const userData = userDoc.data();

        // If the target user is banned, hide their profile from every other
        // visitor — no bio, no avatar, no items, nothing that could keep
        // their persona alive on the platform. Suspended is intentionally
        // NOT treated this way: suspended users are still reachable and
        // their existing content stays visible; the suspension only pauses
        // writes. The user themselves can still see their own profile —
        // otherwise they'd have no way to see what a visitor sees, which
        // is confusing during an appeal.
        const targetModerationState = userData.moderationState as
          | 'active' | 'suspended' | 'banned' | undefined;
        if (targetModerationState === 'banned' && !isOwn) {
          setError('This account is unavailable');
          setLoading(false);
          return;
        }

        // Build profile object from either nested or root-level data
        const profileData: UserProfile = {
          username: userData.profile?.username || userData.username || username,
          displayName: userData.profile?.displayName || userData.displayName || username,
          bio: userData.profile?.bio || userData.bio || '',
          avatarUrl: userData.profile?.avatarUrl || userData.avatarUrl || '',
          bannerUrl: userData.profile?.bannerUrl || userData.bannerUrl || '',
          socialLinks: userData.profile?.socialLinks || userData.socialLinks || {},
          isCollectionPublic: userData.isCollectionPublic,
          privacy: userData.privacy
        };

        setProfile(profileData);

        // Check if profile is public (or if it's own profile)
        const isPublic = profileData.privacy?.isPublic || profileData.isCollectionPublic || false;

        if (!isPublic && !isOwn) {
          setError('This profile is private');
          setLoading(false);
          return;
        }

        // Load brand categories first (needed for stats calculation).
        // Not a subscription — brand categories are edited from Settings
        // and never change during a profile view, so a one-shot fetch is
        // sufficient and keeps the reactive path below focused on items.
        let loadedBrandCategories: Record<string, BrandCategory> = {};
        try {
          const categoriesRef = collection(db, 'users', foundUserId, 'brandCategories');
          const categoriesSnapshot = await getDocs(categoriesRef);
          categoriesSnapshot.forEach((doc) => {
            const data = doc.data();
            loadedBrandCategories[data.brand] = data.category;
          });
        } catch (catError) {
        }

        // Real-time items subscription — every add/edit/remove on the
        // user's items collection refires this callback, so profile
        // stats stay accurate without navigating away and back. The
        // page used to fetch once with getDocs() at mount, so newly
        // added items didn't show up in the counts until the profile
        // remounted.
        const itemsRef = collection(db, 'users', foundUserId, 'items');
        unsubItems = onSnapshot(itemsRef, (itemsSnapshot) => {
          const loadedItems: CollectionItem[] = [];
          itemsSnapshot.forEach((doc) => {
            loadedItems.push({ id: doc.id, ...doc.data() } as CollectionItem);
          });
          setItems(loadedItems);

          // Filter out destashed and deleted items for stats
          const activeItems = loadedItems.filter(i => !i.isDestashed && !i.isDeleted);
          const destashedItems = loadedItems.filter(i => i.isDestashed && !i.isDeleted);
          const listedItems = loadedItems.filter(i => i.isForDestash && !i.isDestashed && !i.isDeleted);

          // Calculate stats by category
          const polishes = activeItems.filter(i => i.itemType === 'polish');
          const plates = activeItems.filter(i => i.itemType === 'plate');
          const tools = activeItems.filter(i => i.itemType === 'tool');
          const decals = activeItems.filter(i => i.itemType === 'transfer' || (i.itemType as string) === 'decal');
          const powders = activeItems.filter(i => i.itemType === 'powder' || (i.itemType as string) === 'buffingPowder');
          const acrylics = activeItems.filter(i => i.itemType === 'acrylic');
          const embellishments = activeItems.filter(i => i.itemType === 'embellishment');

          const uniqueBrandsSet = new Set(activeItems.map(i => i.brand).filter(Boolean));

          const favorites = activeItems.filter(i => i.isFavorite);
          const triedItems = activeItems.filter(i => (i.timesUsed || 0) > 0);

          // Calculate total value (from prices entered)
          const totalValue = activeItems.reduce((sum, item) => {
            const price = item.price || 0;
            const qty = item.quantity || 1;
            return sum + (price * qty);
          }, 0);

          // Count polishes by brand category — ONLY brands that have polishes
          let indieCount = 0;
          let boutiqueCount = 0;
          let mainstreamCount = 0;
          let uncategorizedCount = 0;

          polishes.forEach(polish => {
            const cat = loadedBrandCategories[polish.brand || ''];
            if (cat === 'indie') indieCount++;
            else if (cat === 'boutique') boutiqueCount++;
            else if (cat === 'mainstream') mainstreamCount++;
            else uncategorizedCount++;
          });

          setStats({
            totalItems: activeItems.length,
            polishCount: polishes.length,
            plateCount: plates.length,
            toolCount: tools.length,
            embellishmentCount: embellishments.length,
            decalCount: decals.length,
            powderCount: powders.length,
            acrylicCount: acrylics.length,
            brandCount: uniqueBrandsSet.size,
            favoriteCount: favorites.length,
            indieCount,
            boutiqueCount,
            mainstreamCount,
            uncategorizedCount,
            // Extended stats
            activeCount: activeItems.length,
            destashedCount: destashedItems.length,
            archivedCount: destashedItems.length, // same as destashed for now
            listedForSaleCount: listedItems.length,
            totalValue,
            triedCount: triedItems.length,
            untriedCount: activeItems.length - triedItems.length
          });

          // Calculate insights
          calculateInsights(activeItems, loadedBrandCategories);

          // Calculate trust badges — recomputed too since destashCount /
          // isCollector depend on the live item set.
          const destashCount = loadedItems.filter(i => i.isForDestash || i.isDestashed).length;
          const memberSince = userData.createdAt?.toDate?.() || userData.profile?.createdAt?.toDate?.() || null;

          setBadges({
            verifiedEmail: true, // Assume verified if they have an account
            memberSince: memberSince,
            isActiveTrader: destashCount >= 5,
            isCollector: activeItems.length >= 100
          });
        });

        // Load vouches for this user (wrapped in try-catch in case rules aren't set up yet)
        try {
          const vouchesRef = collection(db, 'users', foundUserId, 'vouches');
          const vouchesSnapshot = await getDocs(vouchesRef);
          const loadedVouches: Vouch[] = [];
          vouchesSnapshot.forEach((doc) => {
            loadedVouches.push({ id: doc.id, ...doc.data() } as Vouch);
          });
          setVouches(loadedVouches);

          // Check if current user has already vouched
          if (currentUser) {
            const alreadyVouched = loadedVouches.some(v => v.fromUserId === currentUser.uid);
            setHasVouched(alreadyVouched);
          }
        } catch (vouchError) {
          // Continue without vouches - profile will still work
        }

        // Load creations for this user
        try {
          const creationsRef = collection(db, 'users', foundUserId, 'creations');
          const creationsSnapshot = await getDocs(creationsRef);
          const loadedCreations: Creation[] = [];
          creationsSnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data() as Omit<Creation, 'id'>;
            // For own profile: show all creations
            // For visitors: show marketplace creations (if destash visible) OR non-marketplace (if creations visible)
            if (isOwn) {
              loadedCreations.push({ ...data, id: docSnapshot.id } as Creation);
            } else {
              // Visitors see all creations - privacy filtering happens at display time
              loadedCreations.push({ ...data, id: docSnapshot.id } as Creation);
            }
          });
          setCreations(loadedCreations);
        } catch (creationsError) {
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error loading profile:', err);
        console.error('Error code:', err?.code);
        console.error('Error message:', err?.message);
        setError(err?.code === 'permission-denied' ? 'Permission denied - check Firebase rules' : 'Error loading profile');
        setLoading(false);
      }
    };

    loadProfile();
    return () => {
      if (unsubItems) unsubItems();
    };
  }, [username]);

  // Group items by "canonical brand identity" — brandSlug when present,
  // trimmed lowercase brand string otherwise. Prevents case- or
  // whitespace-only duplicates ("Cracked" vs "Cracked ") from splitting
  // Top Brands into separate rows. Display name is the mode of raw
  // `brand` values seen in the group so what the user reads is what they
  // (or their fellow user) actually typed.
  const bucketBrands = (
    items: CollectionItem[],
    valueOf: (i: CollectionItem) => number,
  ): Array<{ name: string; count: number }> => {
    const groups = new Map<string, { display: Map<string, number>; total: number }>();
    for (const item of items) {
      if (!item.brand) continue;
      const v = valueOf(item);
      if (v <= 0) continue;
      const raw = String(item.brand);
      const key =
        (item as any).brandSlug as string | undefined || raw.trim().toLowerCase();
      if (!key) continue;
      const group = groups.get(key) ?? { display: new Map(), total: 0 };
      group.total += v;
      group.display.set(raw, (group.display.get(raw) ?? 0) + 1);
      groups.set(key, group);
    }
    return Array.from(groups.values()).map((g) => {
      let best = '';
      let bestN = -1;
      for (const [name, n] of g.display.entries()) {
        if (n > bestN) {
          best = name;
          bestN = n;
        }
      }
      return { name: best, count: g.total };
    });
  };

  // Calculate collection insights
  const calculateInsights = (activeItems: CollectionItem[], brandCats: Record<string, BrandCategory>) => {
    // Overall top brands by count
    const topBrands = bucketBrands(activeItems, () => 1)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Most used item (overall)
    const mostUsedItem = activeItems
      .filter(i => (i.timesUsed || 0) > 0)
      .sort((a, b) => (b.timesUsed || 0) - (a.timesUsed || 0))[0] || null;

    // Top used brand (by total uses) — same brand-identity grouping as
    // topBrands so a single "Cracked" wins even if some items carry a
    // whitespace/case-variant spelling.
    const usedBuckets = bucketBrands(activeItems, (i) => i.timesUsed ?? 0)
      .sort((a, b) => b.count - a.count);
    const topUsedBrand = usedBuckets[0]
      ? { name: usedBuckets[0].name, totalUses: usedBuckets[0].count }
      : null;

    // Overall percentages
    const triedItems = activeItems.filter(i => (i.timesUsed || 0) > 0).length;
    const percentTried = activeItems.length > 0 ? Math.round((triedItems / activeItems.length) * 100) : 0;
    
    const favoriteItems = activeItems.filter(i => i.isFavorite).length;
    const percentFavorites = activeItems.length > 0 ? Math.round((favoriteItems / activeItems.length) * 100) : 0;
    
    const destashItems = activeItems.filter(i => i.isForDestash).length;
    const percentDestash = activeItems.length > 0 ? Math.round((destashItems / activeItems.length) * 100) : 0;

    // Total uses
    const totalUses = activeItems.reduce((sum, item) => sum + (item.timesUsed || 0), 0);

    // === PER-CATEGORY STATS ===
    const categoryConfigs = [
      { itemTypes: ['polish'], label: 'Polish', emoji: '💅', key: 'polish' },
      { itemTypes: ['plate'], label: 'Plates', emoji: '🔲', key: 'plate' },
      { itemTypes: ['powder', 'buffingPowder'], label: 'Powders', emoji: '✨', key: 'powder' },
      { itemTypes: ['tool'], label: 'Tools', emoji: '🔧', key: 'tool' },
      { itemTypes: ['transfer', 'decal'], label: 'Transfers', emoji: '🦋', key: 'decal' },
      { itemTypes: ['embellishment'], label: 'Embellishments', emoji: '💎', key: 'embellishment' },
      { itemTypes: ['acrylic'], label: 'Acrylics', emoji: '💪', key: 'acrylic' },
    ];

    const categoryStats: CategoryStats[] = [];

    categoryConfigs.forEach(config => {
      const categoryItems = activeItems.filter(i => config.itemTypes.includes(i.itemType || ''));
      
      if (categoryItems.length === 0) return; // Skip empty categories

      // Top 3 brands for this category — same brand-identity grouping.
      const catTopBrands = bucketBrands(categoryItems, () => 1)
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);

      // Most used item in category
      const catMostUsed = categoryItems
        .filter(i => (i.timesUsed || 0) > 0)
        .sort((a, b) => (b.timesUsed || 0) - (a.timesUsed || 0))[0] || null;

      // Tried stats
      const catTried = categoryItems.filter(i => (i.timesUsed || 0) > 0).length;
      const catPercentTried = categoryItems.length > 0 ? Math.round((catTried / categoryItems.length) * 100) : 0;

      // Favorites
      const catFavorites = categoryItems.filter(i => i.isFavorite).length;

      // Total uses
      const catTotalUses = categoryItems.reduce((sum, item) => sum + (item.timesUsed || 0), 0);

      const stat: CategoryStats = {
        itemType: config.key,
        label: config.label,
        emoji: config.emoji,
        count: categoryItems.length,
        topBrands: catTopBrands,
        mostUsedItem: catMostUsed,
        triedCount: catTried,
        untriedCount: categoryItems.length - catTried,
        percentTried: catPercentTried,
        favoritesCount: catFavorites,
        totalUses: catTotalUses,
      };

      // Polish-specific: brand type breakdown
      if (config.key === 'polish') {
        let indieCount = 0;
        let boutiqueCount = 0;
        let mainstreamCount = 0;
        let uncategorizedCount = 0;
        
        // Track unique brands per category
        const indieBrands = new Set<string>();
        const boutiqueBrands = new Set<string>();
        const mainstreamBrands = new Set<string>();
        const uncategorizedBrands = new Set<string>();

        categoryItems.forEach(polish => {
          const brand = polish.brand || '';
          const cat = brandCats[brand];
          if (cat === 'indie') {
            indieCount++;
            if (brand) indieBrands.add(brand);
          } else if (cat === 'boutique') {
            boutiqueCount++;
            if (brand) boutiqueBrands.add(brand);
          } else if (cat === 'mainstream') {
            mainstreamCount++;
            if (brand) mainstreamBrands.add(brand);
          } else {
            uncategorizedCount++;
            if (brand) uncategorizedBrands.add(brand);
          }
        });

        stat.indieCount = indieCount;
        stat.boutiqueCount = boutiqueCount;
        stat.mainstreamCount = mainstreamCount;
        stat.uncategorizedCount = uncategorizedCount;
        stat.indieBrandCount = indieBrands.size;
        stat.boutiqueBrandCount = boutiqueBrands.size;
        stat.mainstreamBrandCount = mainstreamBrands.size;
        stat.uncategorizedBrandCount = uncategorizedBrands.size;
      }

      categoryStats.push(stat);
    });

    setInsights({
      topBrands,
      mostUsedItem,
      topUsedBrand,
      percentTried,
      percentFavorites,
      percentDestash,
      totalUses,
      categoryStats
    });
  };

  // Get favorites for teaser
  const getFavorites = () => {
    return items
      .filter(i => i.isFavorite && !i.isDestashed && !i.isDeleted)
      .slice(0, 6);
  };

  // Get rare/limited edition items for teaser
  const getRareLE = () => {
    return items
      .filter(i => i.isRareLE && !i.isDestashed && !i.isDeleted)
      .slice(0, 6);
  };

  // Get swatched items for teaser
  const getSwatched = () => {
    return items
      .filter(i => i.swatched && !i.isDestashed && !i.isDeleted)
      .sort((a, b) => {
        // Sort by most recently updated/created
        const dateA = (a.updatedAt as any)?.toDate?.() || (a.createdAt as any)?.toDate?.() || new Date(0);
        const dateB = (b.updatedAt as any)?.toDate?.() || (b.createdAt as any)?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 6);
  };

  // Get destash items for teaser
  const getDestashItems = () => {
    return items
      .filter(i => i.isForDestash && !i.isDestashed && !i.isDeleted)
      .slice(0, 6);
  };

  // Get wishlist items for teaser
  const getWishlistItems = () => {
    // Priority order: unicorn (highest) > diso > iso (lowest)
    const priorityOrder: Record<string, number> = {
      'unicorn': 0,
      'diso': 1,
      'iso': 2
    };

    return items
      .filter(i => i.onWishlist && !i.isDestashed && !i.isDeleted)
      .sort((a, b) => {
        const priorityA = priorityOrder[a.isoPriority || 'iso'] ?? 2;
        const priorityB = priorityOrder[b.isoPriority || 'iso'] ?? 2;
        return priorityA - priorityB;
      })
      .slice(0, 6);
  };

  // Get recent additions (exclude wishlist items)
  const getRecentAdditions = () => {
    return items
      .filter(i => !i.isDestashed && !i.isDeleted && !i.onWishlist)
      .sort((a, b) => {
        const dateA = (a.createdAt as any)?.toDate?.() || new Date(0);
        const dateB = (b.createdAt as any)?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 6);
  };

  // Get nail art creations for teaser (manis, pedis, etc - NOT for sale)
  const getNailArtCreations = () => {
    return creations
      .filter(c => !c.listedOnMarketplace)
      .sort((a, b) => {
        const dateA = a.dateCreated || '';
        const dateB = b.dateCreated || '';
        return dateB.localeCompare(dateA);
      })
      .slice(0, 6);
  };

  // Get marketplace creations for teaser (press-ons, jewelry for sale)
  const getMarketplaceCreations = () => {
    return creations
      .filter(c => c.listedOnMarketplace && c.marketplaceStatus !== 'sold')
      .slice(0, 6);
  };

  // Get creation display image
  const getCreationImage = (creation: Creation): string | null => {
    if (creation.photos && creation.photos.length > 0) {
      const defaultPhoto = creation.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : creation.photos[0].url;
    }
    return null;
  };

  // Get category label for marketplace creation
  const getCreationCategoryLabel = (category?: string) => {
    switch (category) {
      case 'pressons': return '💅 Press-Ons';
      case 'jewelry': return '💍 Jewelry';
      case 'other': return '✨ Other';
      default: return '✨ Creation';
    }
  };

  // Get type label for nail art creation
  const getCreationTypeLabel = (creationType?: string) => {
    switch (creationType) {
      case 'mani': return '💅 Mani';
      case 'pedi': return '🦶 Pedi';
      case 'nail-art': return '🎨 Nail Art';
      case 'practice': return '📝 Practice';
      case 'press-ons': return '✨ Press-Ons';
      case 'jewelry': return '💍 Jewelry';
      case 'other': return '📦 Other';
      default: return '💅 Mani';
    }
  };

  // Format social link URL
  const formatSocialUrl = (platform: string, value: string): string => {
    if (!value) return '';
    
    // If already a full URL, return as-is
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    
    // Remove @ if present
    const cleanValue = value.replace(/^@/, '');
    
    switch (platform) {
      case 'instagram':
        return `https://instagram.com/${cleanValue}`;
      case 'youtube':
        return value.includes('youtube.com') ? value : `https://youtube.com/${cleanValue}`;
      case 'tiktok':
        return `https://tiktok.com/@${cleanValue}`;
      case 'facebook':
        return `https://facebook.com/${cleanValue}`;
      case 'blog':
        return value.startsWith('http') ? value : `https://${value}`;
      case 'shop':
        return value.startsWith('http') ? value : `https://${value}`;
      default:
        return value;
    }
  };

  // Check if any social links exist
  const hasSocialLinks = profile?.socialLinks && (
    profile.socialLinks.instagram ||
    profile.socialLinks.youtube ||
    profile.socialLinks.tiktok ||
    profile.socialLinks.facebook ||
    profile.socialLinks.blog ||
    profile.socialLinks.shop
  );

  // Check if messaging is allowed
  const canMessage = !isOwnProfile && 
    (profile?.privacy?.allowMessages !== false) && // Default to true if not set
    userId;

  // Start or find existing conversation
  const handleStartConversation = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser || !userId || !profile) return;

    setIsStartingChat(true);

    try {
      // Check if conversation already exists between these users
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', currentUser.uid)
      );
      
      const snapshot = await getDocs(q);
      let existingConversationId: string | null = null;

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.participants.includes(userId)) {
          existingConversationId = doc.id;
        }
      });

      if (existingConversationId) {
        // Navigate to existing conversation
        navigate(`/messages/${existingConversationId}`);
      } else {
        // Create new conversation
        const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const currentUserData = currentUserDoc.data();

        const newConversation = {
          participants: [currentUser.uid, userId],
          participantInfo: {
            [currentUser.uid]: {
              displayName: currentUserData?.displayName || currentUserData?.profile?.displayName || 'Unknown',
              username: currentUserData?.username || currentUserData?.profile?.username || '',
              avatarUrl: currentUserData?.avatarUrl || currentUserData?.profile?.avatarUrl || ''
            },
            [userId]: {
              displayName: profile.displayName || profile.username,
              username: profile.username,
              avatarUrl: profile.avatarUrl || ''
            }
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          pinnedItems: [],
          pinnedCreations: []
        };

        const docRef = await addDoc(conversationsRef, newConversation);
        navigate(`/messages/${docRef.id}`);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setIsStartingChat(false);
    }
  };

  // Handle vouching for a user
  const handleVouch = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser || !userId || isOwnProfile || hasVouched) return;

    setIsVouching(true);

    try {
      // Get current user's info
      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const currentUserData = currentUserDoc.data();

      // Create vouch document
      const vouchData = {
        fromUserId: currentUser.uid,
        fromUsername: currentUserData?.username || currentUserData?.profile?.username || '',
        fromDisplayName: currentUserData?.displayName || currentUserData?.profile?.displayName || 'Anonymous',
        fromAvatarUrl: currentUserData?.avatarUrl || currentUserData?.profile?.avatarUrl || '',
        createdAt: serverTimestamp()
      };

      // Add to user's vouches subcollection
      const vouchesRef = collection(db, 'users', userId, 'vouches');
      const docRef = await addDoc(vouchesRef, vouchData);

      // Update local state
      setVouches(prev => [...prev, { id: docRef.id, ...vouchData, createdAt: Timestamp.now() }]);
      setHasVouched(true);

    } catch (error) {
      console.error('Error vouching for user:', error);
      alert('Failed to vouch. Please try again.');
    } finally {
      setIsVouching(false);
    }
  };

  // Format member since date
  const formatMemberSince = (date: Date | null): string => {
    if (!date) return 'Unknown';
    const year = date.getFullYear();
    const month = date.toLocaleString('default', { month: 'short' });
    return `${month} ${year}`;
  };

  // Get item display image
  const getItemImage = (item: CollectionItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    if (item.swatchPhotos && item.swatchPhotos.length > 0) return item.swatchPhotos[0];
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-glass-secondary font-body">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const getErrorMessage = () => {
      if (error === 'User not found') {
        return "This user doesn't exist or the username is incorrect.";
      }
      if (error === 'This profile is private') {
        return "This user's profile is set to private.";
      }
      if (error === 'This account is unavailable') {
        // Deliberately vague — don't confirm or deny that the user was banned.
        return "This account is no longer available.";
      }
      if (error.includes('Permission denied')) {
        return "Unable to load this profile. This may be a permissions issue - please contact support if this persists.";
      }
      return "Something went wrong loading this profile. Please try again later.";
    };

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card p-8 max-w-md text-center">
          <div className="text-6xl mb-4">
            {error === 'This account is unavailable' ? '🚫' : '😢'}
          </div>
          <h2 className="text-2xl font-bold text-glass mb-2 font-heading">
            {error === 'User not found' ? 'User Not Found' :
             error === 'This profile is private' ? 'Private Profile' :
             error === 'This account is unavailable' ? 'Account Unavailable' :
             'Error Loading Profile'}
          </h2>
          <p className="text-glass-secondary mb-6 font-body">
            {getErrorMessage()}
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default font-body"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const favorites = getFavorites();
  const rareLE = getRareLE();
  const swatched = getSwatched();
  const destashItems = getDestashItems();
  const wishlistItems = getWishlistItems();
  const recentAdditions = getRecentAdditions();
  const marketplaceCreations = getMarketplaceCreations();
  const nailArtCreations = getNailArtCreations();

  // When previewing as visitor, treat as non-owner for privacy checks
  const showAsOwner = isOwnProfile && !isPreviewingAsVisitor;

  return (
    <div className="min-h-screen pb-12">
      {/* Visibility Banner - Only for profile owner */}
      {isOwnProfile && profile?.username && (
        <div className="max-w-4xl mx-auto px-4 pt-6">
          <PageVisibilityBanner 
            isPublic={profile?.privacy?.isPublic !== false}
            publicPath={`/profile/${profile.username}`}
            pageType="profile"
            onPreview={() => {
              const newPreviewState = !isPreviewingAsVisitor;
              setIsPreviewingAsVisitor(newPreviewState);
              // Update URL to match preview state
              if (newPreviewState) {
                setSearchParams({ preview: 'true' });
              } else {
                setSearchParams({});
              }
            }}
            isPreviewMode={isPreviewingAsVisitor}
          />
        </div>
      )}

      {/* When previewing a PRIVATE profile, show what visitors would see */}
      {isPreviewingAsVisitor && !profile?.privacy?.isPublic && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="glass-card p-8 max-w-md text-center">
            <div className="text-6xl mb-4">😰</div>
            <h2 className="text-2xl font-bold text-glass mb-2 font-heading">
              Private Profile
            </h2>
            <p className="text-glass-secondary mb-6 font-body">
              This user's profile is set to private.
            </p>
            <button
              onClick={() => {
                setIsPreviewingAsVisitor(false);
                setSearchParams({});
              }}
              className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-default font-body"
            >
              Exit Preview
            </button>
          </div>
        </div>
      )}

      {/* Only show profile content if NOT previewing a private profile */}
      {!(isPreviewingAsVisitor && !profile?.privacy?.isPublic) && (
      <>
      {/* Profile Header - GLASSMORPHIC */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <div className="glass-card overflow-hidden mb-6">
          {/* Banner */}
          <div className="h-40 sm:h-48 bg-gradient-to-r from-fuchsia-400 to-purple-500 relative">
            {profile?.bannerUrl && (
              <img 
                src={profile.bannerUrl} 
                alt="Profile banner" 
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* Profile Info */}
          <div className="px-6 pb-6 relative">
            {/* Avatar */}
            <div className="absolute -top-16 left-6">
              <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg rainbow-gradient overflow-hidden">
                {profile?.avatarUrl ? (
                  <img 
                    src={profile.avatarUrl} 
                    alt={profile.displayName} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl text-white">
                    💅
                  </div>
                )}
              </div>
            </div>

            {/* Name & Edit Button */}
            <div className="pt-20">
              {/* Name row with Edit button */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h1 className="text-2xl font-bold text-glass font-heading">
                    {profile?.displayName || profile?.username}
                  </h1>
                  <p className="text-glass-secondary text-sm font-body">
                    @{profile?.username}
                  </p>
                </div>
                
                {/* Edit Button (owner only) - Right next to name */}
                {isOwnProfile && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 text-glass rounded-lg hover:bg-slate-600 transition-default font-medium text-sm font-body flex-shrink-0"
                  >
                    ✏️ Edit Profile
                  </button>
                )}
              </div>
                
              {/* Bio */}
              {profile?.bio && (
                <p className="mt-3 text-glass-secondary font-body">
                  {profile.bio}
                </p>
              )}

              {/* Social Links */}
              {hasSocialLinks && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {profile?.socialLinks?.instagram && (
                    <a
                      href={formatSocialUrl('instagram', profile.socialLinks.instagram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <Instagram className="icon-sm" />
                      Instagram
                    </a>
                  )}
                  {profile?.socialLinks?.youtube && (
                    <a
                      href={formatSocialUrl('youtube', profile.socialLinks.youtube)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <Youtube className="icon-sm" />
                      YouTube
                    </a>
                  )}
                  {profile?.socialLinks?.tiktok && (
                    <a
                      href={formatSocialUrl('tiktok', profile.socialLinks.tiktok)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                      TikTok
                    </a>
                  )}
                  {profile?.socialLinks?.facebook && (
                    <a
                      href={formatSocialUrl('facebook', profile.socialLinks.facebook)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </a>
                  )}
                  {profile?.socialLinks?.blog && (
                    <a
                      href={formatSocialUrl('blog', profile.socialLinks.blog)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <Globe className="icon-sm" />
                      Blog
                    </a>
                  )}
                  {profile?.socialLinks?.shop && (
                    <a
                      href={formatSocialUrl('shop', profile.socialLinks.shop)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <path d="M16 10a4 4 0 0 1-8 0"></path>
                      </svg>
                      Shop
                    </a>
                  )}
                </div>
              )}

              {/* Message/Follow Buttons (visitor only) */}
              {!isOwnProfile && (
                <div className="flex gap-2 mt-4">
                  {/* Follow Button - Always show for non-owners */}
                  {userId && (
                    <FavoriteSellerButton
                      sellerId={userId}
                      sellerUsername={profile?.username}
                      sellerAvatar={profile?.avatarUrl}
                      currentUserId={getCurrentUser()?.uid || null}
                      variant="button"
                    />
                  )}
                  {/* Message Button */}
                  {canMessage && (
                    <button
                      onClick={handleStartConversation}
                      disabled={isStartingChat}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-default font-medium text-sm disabled:opacity-50 font-body"
                    >
                      {isStartingChat ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <MessageSquare className="icon-sm" />
                          Message
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Stats Row */}
            {(showAsOwner || profile?.privacy?.showStats || profile?.isCollectionPublic) && stats && (
              <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-100">
                <div className="text-center">
                  <div className="text-2xl font-bold text-glass">{stats.totalItems.toLocaleString()}</div>
                  <div className="text-sm text-glass-secondary">Items</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-glass">{stats.polishCount.toLocaleString()}</div>
                  <div className="text-sm text-glass-secondary">Polishes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-glass">{stats.plateCount.toLocaleString()}</div>
                  <div className="text-sm text-glass-secondary">Plates</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-glass">{stats.brandCount.toLocaleString()}</div>
                  <div className="text-sm text-glass-secondary">Brands</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-glass">{stats.favoriteCount.toLocaleString()}</div>
                  <div className="text-sm text-glass-secondary">Favorites</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust & Vouches Section - GLASSMORPHIC */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
              🏆 Trust & Reputation
            </h2>
            {!isOwnProfile && !hasVouched && userId && (
              <button
                onClick={handleVouch}
                disabled={isVouching}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 transition-default font-medium text-sm disabled:opacity-50 font-body"
              >
                {isVouching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Vouching...
                  </>
                ) : (
                  <>
                    <Heart className="icon-sm" />
                    Vouch for {profile?.displayName?.split(' ')[0] || 'them'}
                  </>
                )}
              </button>
            )}
            {hasVouched && !isOwnProfile && (
              <span className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/30 rounded-full text-sm font-medium">
                ✓ You vouched
              </span>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {badges.verifiedEmail && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-full text-sm font-medium">
                <Shield className="icon-sm" />
                Verified
              </span>
            )}
            {badges.memberSince && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-slate-500 text-glass rounded-full text-sm font-medium">
                <Calendar className="icon-sm" />
                Member since {formatMemberSince(badges.memberSince)}
              </span>
            )}
            {badges.isActiveTrader && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-full text-sm font-medium">
                <Package className="icon-sm" />
                Active Trader
              </span>
            )}
          </div>


          {/* Vouches */}
          <div className="border-t border-slate-600 pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">💜</span>
                <div>
                  <div className="text-xl font-bold text-glass">{vouches.length}</div>
                  <div className="text-sm text-glass-secondary">
                    {vouches.length === 1 ? 'vouch from a collector' : 'vouches from collectors'}
                  </div>
                </div>
              </div>
              {vouches.length > 0 && (
                <button
                  onClick={() => setShowVouchList(!showVouchList)}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium font-body"
                >
                  {showVouchList ? 'Hide' : 'View all'}
                </button>
              )}
            </div>

            {/* Vouch list (expandable) */}
            {showVouchList && vouches.length > 0 && (
              <div className="mt-4 space-y-2">
                {vouches.map((vouch) => (
                  <Link
                    key={vouch.id}
                    to={`/profile/${vouch.fromUsername || vouch.fromUserId}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700 transition-default"
                  >
                    {vouch.fromAvatarUrl ? (
                      <img
                        src={vouch.fromAvatarUrl}
                        alt={vouch.fromDisplayName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold">
                        {vouch.fromDisplayName?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-glass">
                        {vouch.fromDisplayName}
                      </div>
                      {vouch.fromUsername && (
                        <div className="text-xs text-glass-muted">@{vouch.fromUsername}</div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Collection Insights - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showStats || profile?.isCollectionPublic) && insights && (
          <div className="glass-card p-6 mb-6">
            <h2 className="text-lg font-bold text-glass mb-4 flex items-center gap-2 font-heading">
              📊 Collection Insights
            </h2>

            {/* Collection Overview Stats */}
            {stats && (showAsOwner || profile?.privacy?.showInsightsOverview !== false) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="glass-card-sm p-4 border-l-4 border-l-green-500 text-center">
                  <div className="text-2xl font-bold text-glass">{stats.activeCount.toLocaleString()}</div>
                  <div className="text-xs text-glass-secondary font-medium">Active Collection</div>
                </div>
                <div className="glass-card-sm p-4 border-l-4 border-l-amber-500 text-center">
                  <div className="text-2xl font-bold text-glass">{stats.listedForSaleCount}</div>
                  <div className="text-xs text-glass-secondary font-medium">Listed for Sale</div>
                </div>
                <div className="glass-card-sm p-4 border-l-4 border-l-pink-500 text-center">
                  <div className="text-2xl font-bold text-glass">{stats.destashedCount}</div>
                  <div className="text-xs text-glass-secondary font-medium">Destashed</div>
                </div>
                <div className="glass-card-sm p-4 border-l-4 border-l-purple-500 text-center">
                  {stats.totalValue > 0 && (showAsOwner || profile?.privacy?.showCollectionValue) ? (
                    <>
                      <div className="text-2xl font-bold text-glass">${stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                      <div className="text-xs text-glass-secondary font-medium">Collection Value</div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-bold text-glass-muted">—</div>
                      <div className="text-xs text-glass-secondary font-medium">Collection Value</div>
                      <div className="text-[10px] text-glass-muted">Private</div>
                    </>
                  )}
                </div>
              </div>
            )}


            {/* Achievement Badges - Swatch & Rare/LE */}
            {(showAsOwner || profile?.privacy?.showCollectorBadges !== false) && stats && (
              (() => {
                // Calculate swatch stats
                const swatchedCount = items.filter(i => i.swatched && !i.isDestashed && !i.isDeleted).length;
                const activePolishes = items.filter(i => i.itemType === 'polish' && !i.isDestashed && !i.isDeleted && !i.onWishlist).length;
                const swatchedPercent = activePolishes > 0 ? Math.round((swatchedCount / activePolishes) * 100) : 0;

                // Calculate rare/LE count
                const rareCount = items.filter(i => i.isRareLE && !i.isDestashed && !i.isDeleted).length;
                
                // Swatch percentage badges
                const swatchPercentBadges = [
                  { name: 'Swatch Starter', emoji: '🎨', threshold: 25, earned: swatchedPercent >= 25 },
                  { name: 'Swatch Star', emoji: '⭐', threshold: 50, earned: swatchedPercent >= 50 },
                  { name: 'Swatch Superstar', emoji: '🌟', threshold: 75, earned: swatchedPercent >= 75 },
                  { name: 'Swatch Sovereign', emoji: '👑', threshold: 90, earned: swatchedPercent >= 90 },
                  { name: 'Completely Coated', emoji: '🏆', threshold: 100, earned: swatchedPercent >= 100 },
                ];
                
                // Swatch volume badges
                const swatchVolumeBadges = [
                  { name: 'Century Club', emoji: '💪', threshold: 100, earned: swatchedCount >= 100 },
                  { name: 'Five Hundred Strong', emoji: '🔥', threshold: 500, earned: swatchedCount >= 500 },
                  { name: 'Thousand Touch', emoji: '✨', threshold: 1000, earned: swatchedCount >= 1000 },
                ];
                
                // Rare/LE badges
                const rareBadges = [
                  { name: 'Rare Recruit', emoji: '💎', threshold: 10, earned: rareCount >= 10 },
                  { name: 'Rarity Ranger', emoji: '🔍', threshold: 25, earned: rareCount >= 25 },
                  { name: 'Rarity Royalty', emoji: '👑', threshold: 50, earned: rareCount >= 50 },
                  { name: 'Legendary Luminary', emoji: '✨', threshold: 100, earned: rareCount >= 100 },
                ];
                
                const earnedSwatchPercent = swatchPercentBadges.filter(b => b.earned);
                const earnedSwatchVolume = swatchVolumeBadges.filter(b => b.earned);
                const earnedRare = rareBadges.filter(b => b.earned);
                const totalEarned = earnedSwatchPercent.length + earnedSwatchVolume.length + earnedRare.length;
                const totalPossible = swatchPercentBadges.length + swatchVolumeBadges.length + rareBadges.length;
                
                // Only show if user has earned at least one badge
                if (totalEarned === 0) return null;
                
                return (
                  <div className="mb-6 p-4 bg-slate-800/40 rounded-lg border border-slate-600">
                    <h3 className="text-sm font-bold text-glass mb-3 font-body flex items-center gap-2">
                      🎖️ Achievement Badges
                      <span className="text-xs text-glass-muted font-normal">
                        ({totalEarned} of {totalPossible} earned)
                      </span>
                    </h3>
                    
                    <div className="flex flex-wrap gap-2">
                      {/* Swatch Completion Badges */}
                      {earnedSwatchPercent.map(badge => (
                        <span 
                          key={badge.name}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs"
                          title={`${badge.name} - ${badge.threshold}% swatched`}
                        >
                          {badge.emoji} <span className="text-glass">{badge.name}</span>
                        </span>
                      ))}
                      
                      {/* Swatch Volume Badges */}
                      {earnedSwatchVolume.map(badge => (
                        <span 
                          key={badge.name}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full text-xs"
                          title={`${badge.name} - ${badge.threshold}+ swatched`}
                        >
                          {badge.emoji} <span className="text-glass">{badge.name}</span>
                        </span>
                      ))}
                      
                      {/* Rare/LE Badges */}
                      {earnedRare.map(badge => (
                        <span 
                          key={badge.name}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-violet-500/20 border border-violet-500/30 rounded-full text-xs"
                          title={`${badge.name} - ${badge.threshold}+ rare/LE items`}
                        >
                          {badge.emoji} <span className="text-glass">{badge.name}</span>
                        </span>
                      ))}
                    </div>
                    
                    {/* Progress hint */}
                    <div className="mt-3 text-xs text-glass-muted">
                      {swatchedPercent}% swatched ({swatchedCount} items) • {rareCount} rare/LE items
                    </div>
                  </div>
                );
              })()
            )}

            {/* Category Cards - sorted by count */}
            {insights.categoryStats && insights.categoryStats.length > 0 && (showAsOwner || profile?.privacy?.showCategoryStats !== false) && (
              <div className="space-y-4">
                {[...insights.categoryStats]
                  .sort((a, b) => b.count - a.count)
                  .map((cat) => {

                  // Get collector tier for this category
                  const tierKey = cat.itemType === 'decal' ? 'tool' : (cat.itemType as 'polish' | 'plate' | 'powder' | 'tool');
                  const tier = getCollectorTier(cat.count, ['polish', 'plate', 'powder', 'tool'].includes(cat.itemType) ? tierKey : 'tool');
                  
                  // Check if collector badges should be shown
                  const showBadges = showAsOwner || profile?.privacy?.showCollectorBadges !== false;
                  
                  // Check if polish brand types should be shown
                  const showBrandTypes = showAsOwner || profile?.privacy?.showPolishBrandTypes !== false;

                  // Get category-specific left border color
                  const getCategoryBorderColor = (itemType: string): string => {
                    const borderColors: Record<string, string> = {
                      polish: 'border-l-pink-500',
                      plate: 'border-l-indigo-500',
                      powder: 'border-l-cyan-500',
                      tool: 'border-l-amber-500',
                      decal: 'border-l-teal-500',
                      embellishment: 'border-l-violet-500',
                      acrylic: 'border-l-rose-500',
                    };
                    return borderColors[itemType] || 'border-l-pink-500';
                  };

                  return (
                    <div
                      key={cat.itemType}
                      className={`glass-card-sm p-5 ${getCategoryBorderColor(cat.itemType)}`}
                    >
                      {/* Header: Category name + tier badge + count */}
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-glass flex items-center gap-2 font-body">
                          <span className="text-2xl">{cat.emoji}</span>
                          {cat.label}
                        </h3>
                        <div className="flex items-center gap-3">
                          {showBadges && tier.tier !== 'none' && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 ${tier.bgColor} ${tier.color} rounded-full text-xs font-bold`}>
                              {tier.emoji} {tier.label}
                            </span>
                          )}
                          <span className="text-2xl font-bold text-glass">
                            {cat.count.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Top Brands Row */}
                      {cat.topBrands.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className="text-sm font-medium text-glass-secondary">Top Brands:</span>
                          {cat.topBrands[0] && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 border-2 border-amber-500 rounded-full text-sm">
                              <span>🥇</span>
                              <span className="font-semibold text-glass">{cat.topBrands[0].name}</span>
                              <span className="text-glass-secondary">({cat.topBrands[0].count})</span>
                            </span>
                          )}
                          {cat.topBrands[1] && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 border-2 border-slate-500 rounded-full text-sm">
                              <span>🥈</span>
                              <span className="font-semibold text-glass">{cat.topBrands[1].name}</span>
                              <span className="text-glass-secondary">({cat.topBrands[1].count})</span>
                            </span>
                          )}
                          {cat.topBrands[2] && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 border-2 border-orange-500 rounded-full text-sm">
                              <span>🥉</span>
                              <span className="font-semibold text-glass">{cat.topBrands[2].name}</span>
                              <span className="text-glass-secondary">({cat.topBrands[2].count})</span>
                            </span>
                          )}
                        </div>
                      )}

                      {/* Most Used Item */}
                      {cat.mostUsedItem && (
                        <div
                          onClick={() => setSelectedItem(cat.mostUsedItem)}
                          className="flex items-center gap-2 mb-3 p-3 border-l-4 border-green-500 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-default"
                        >
                          <span className="text-sm font-medium text-glass">🏆 Most Used:</span>
                          <span className="font-semibold text-glass">{cat.mostUsedItem.name}</span>
                          <span className="text-sm text-glass-secondary">by {cat.mostUsedItem.brand}</span>
                          <span className="ml-auto px-2 py-1 bg-green-500/20 border border-green-500 text-glass rounded-full text-xs font-bold">
                            {cat.mostUsedItem.timesUsed}× used
                          </span>
                        </div>
                      )}

                      {/* Stats Row */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="text-glass-secondary">
                          <span className="font-semibold text-glass">{cat.percentTried}%</span> tried
                        </span>
                        <span className="text-glass-muted">•</span>
                        <span className="text-glass-secondary">
                          <span className="font-semibold text-glass">{cat.untriedCount.toLocaleString()}</span> untried
                        </span>
                        <span className="text-glass-muted">•</span>
                        <span className="text-glass-secondary">
                          <span className="font-semibold text-glass">{cat.favoritesCount}</span> treasures
                        </span>
                        {cat.totalUses > 0 && (
                          <>
                            <span className="text-glass-muted">•</span>
                            <span className="text-glass-secondary">
                              <span className="font-semibold text-glass">{cat.totalUses.toLocaleString()}</span> total uses
                            </span>
                          </>
                        )}
                      </div>

                      {/* Polish-specific: Brand Type Breakdown */}
                      {cat.itemType === 'polish' && showBrandTypes && (cat.indieBrandCount !== undefined || cat.boutiqueBrandCount !== undefined || cat.mainstreamBrandCount !== undefined) && (
                        <div className="mt-4 pt-4 border-t border-pink-200/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-glass-secondary">Brand Types:</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(cat.indieBrandCount || 0) > 0 && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-purple-500 bg-purple-500/20 text-glass rounded-full text-sm font-medium">
                                🎨 {cat.indieBrandCount} Indie <span className="text-glass-secondary font-normal">({cat.indieCount} polishes)</span>
                              </span>
                            )}
                            {(cat.boutiqueBrandCount || 0) > 0 && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-pink-500 bg-pink-500/20 text-glass rounded-full text-sm font-medium">
                                ✨ {cat.boutiqueBrandCount} Boutique <span className="text-glass-secondary font-normal">({cat.boutiqueCount} polishes)</span>
                              </span>
                            )}
                            {(cat.mainstreamBrandCount || 0) > 0 && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-blue-500 bg-blue-500/20 text-glass rounded-full text-sm font-medium">
                                🏪 {cat.mainstreamBrandCount} Mainstream <span className="text-glass-secondary font-normal">({cat.mainstreamCount} polishes)</span>
                              </span>
                            )}
                            {(cat.uncategorizedBrandCount || 0) > 0 && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-slate-500 bg-slate-500/20 text-glass rounded-full text-sm font-medium">
                                ❓ {cat.uncategorizedBrandCount} Uncategorized <span className="text-glass-secondary font-normal">({cat.uncategorizedCount} polishes)</span>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Creations Teaser (Nail Art) - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showCreations) && nailArtCreations.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                💅 Creations
              </h2>
              <Link 
                to={`/creations/${username}?filter=nailart`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all {creations.filter(c => !c.listedOnMarketplace).length} →
              </Link>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {nailArtCreations.map((creation) => (
                <div 
                  key={creation.id} 
                  onClick={() => setSelectedCreation(creation)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100 shadow-sm group-hover:shadow-md transition-shadow relative">
                    {getCreationImage(creation) ? (
                      <img
                        src={getCreationImage(creation)!}
                        alt={creation.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        💅
                      </div>
                    )}
                    {/* Type badge */}
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-white/90 rounded text-[10px] font-bold text-pink-600">
                      {getCreationTypeLabel(creation.creationType)}
                    </span>
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {creation.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* For Sale/Trade Teaser - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showDestashList) && destashItems.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                🏷️ Destash Items
              </h2>
              <Link 
                to={isOwnProfile ? "/collection?filter=destash&perPage=25" : `/marketplace?seller=${username}`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See {items.filter(i => i.isForDestash && !i.isDestashed && !i.isDeleted).length} available →
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {destashItems.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-orange-100 to-yellow-100 shadow-sm group-hover:shadow-md transition-shadow">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        🏷️
                      </div>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {item.name}
                    </p>
                    <p className="text-xs text-glass-secondary line-clamp-1">
                      {item.brand}
                    </p>
                    {item.destashPrice && (
                      <p className="text-xs font-semibold text-green-600">
                        ${item.destashPrice}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Favorites Teaser - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showFavorites) && favorites.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                💖 Favorites
              </h2>
              <Link 
                to={isOwnProfile ? "/collection?filter=favorites&perPage=25" : `/profile/${username}/collection?filter=favorites`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all {items.filter(i => i.isFavorite && !i.isDestashed && !i.isDeleted).length} →
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {favorites.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-fuchsia-100 to-purple-100 shadow-sm group-hover:shadow-md transition-shadow">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        💅
                      </div>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {item.name}
                    </p>
                    <p className="text-xs text-glass-secondary line-clamp-1">
                      {item.brand}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* For Sale Teaser (Marketplace Creations) - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showDestashList) && marketplaceCreations.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                🛒 For Sale
              </h2>
              <Link 
                to={`/creations/${username}?filter=marketplace`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all {creations.filter(c => c.listedOnMarketplace).length} →
              </Link>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {marketplaceCreations.map((creation) => (
                <div 
                  key={creation.id} 
                  onClick={() => setSelectedCreation(creation)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 shadow-sm group-hover:shadow-md transition-shadow relative">
                    {getCreationImage(creation) ? (
                      <img
                        src={getCreationImage(creation)!}
                        alt={creation.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        ✨
                      </div>
                    )}
                    {/* Category badge */}
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-white/90 rounded text-[10px] font-bold text-purple-600">
                      {getCreationCategoryLabel(creation.marketplaceCategory)}
                    </span>
                    {/* Price badge */}
                    {creation.price && (
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-green-500 text-white rounded text-[10px] font-bold">
                        ${creation.price}
                      </span>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {creation.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rare/Limited Edition Teaser - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showRareLE) && rareLE.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                💎 Rare & Limited Editions
              </h2>
              <Link 
                to={isOwnProfile ? "/collection?filter=rare-le&perPage=25" : `/profile/${username}/collection?filter=rare-le`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all {items.filter(i => i.isRareLE && !i.isDestashed && !i.isDeleted).length} →
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {rareLE.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-violet-100 to-indigo-100 shadow-sm group-hover:shadow-md transition-shadow">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        💎
                      </div>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {item.name}
                    </p>
                    <p className="text-xs text-glass-secondary line-clamp-1">
                      {item.brand}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Additions Teaser - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showCollection) && recentAdditions.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                ✨ Recent Additions
              </h2>
              <Link 
                to={isOwnProfile ? "/collection?sort=recent&perPage=25" : `/profile/${username}/collection?sort=recent`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all recent →
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {recentAdditions.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 shadow-sm group-hover:shadow-md transition-shadow">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        💅
                      </div>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {item.name}
                    </p>
                    <p className="text-xs text-glass-secondary line-clamp-1">
                      {item.brand}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Swatched Items Teaser - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showSwatched) && swatched.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                🎨 Swatched
              </h2>
              <Link 
                to={isOwnProfile ? "/collection?filter=swatched&perPage=25" : `/profile/${username}/collection?filter=swatched`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all {items.filter(i => i.swatched && !i.isDestashed && !i.isDeleted).length} →
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {swatched.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  className="group relative cursor-pointer"
                >
                  <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100 shadow-sm group-hover:shadow-md transition-shadow">
                    {getItemImage(item) ? (
                      <img
                        src={getItemImage(item)!}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">
                        🎨
                      </div>
                    )}
                  </div>
                  <div className="mt-1">
                    <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                      {item.name}
                    </p>
                    <p className="text-xs text-glass-secondary line-clamp-1">
                      {item.brand}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Wishlist Items Teaser - GLASSMORPHIC */}
        {(showAsOwner || profile?.privacy?.showWishlist) && wishlistItems.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-glass flex items-center gap-2 font-heading">
                ⭐ Wishlist Items
              </h2>
              <Link 
                to={`/wishlist/${username}`}
                className="text-sm text-glass-secondary hover:text-glass font-medium hover:underline"
              >
                See all {items.filter(i => i.onWishlist && !i.isDestashed && !i.isDeleted).length} →
              </Link>
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {wishlistItems.map((item) => {
                // Get ISO priority badge style using centralized PriorityColors
                const key = (item.isoPriority || 'iso') as PriorityKey;
                const colors = PriorityColors[key] || PriorityColors.iso;
                const priorityBadge = { bg: colors.bg, text: colors.label };
                
                // Show contact button if: not own profile, logged in, and owner allows contact
                const currentUser = getCurrentUser();
                const showContactButton = !isOwnProfile && !isPreviewingAsVisitor && currentUser && profile?.privacy?.allowWishlistContact;
                
                return (
                  <div 
                    key={item.id} 
                    className="group relative"
                  >
                    {/* Clickable image area */}
                    <div 
                      onClick={() => setSelectedItem(item)}
                      className="cursor-pointer"
                    >
                      <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-pink-100 to-rose-100 shadow-sm group-hover:shadow-md transition-shadow relative">
                        {getItemImage(item) ? (
                          <img
                            src={getItemImage(item)!}
                            alt={item.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-3xl">
                            💝
                          </div>
                        )}
                        
                        {/* ISO Priority Badge */}
                        <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${priorityBadge.bg}`}>
                          {priorityBadge.text}
                        </span>
                      </div>
                      <div className="mt-1">
                        <p className="text-xs font-medium text-glass line-clamp-1 font-body">
                          {item.name}
                        </p>
                        <p className="text-xs text-glass-muted line-clamp-1">
                          {item.brand}
                        </p>
                      </div>
                    </div>
                    
                    {/* "I Have This!" Button - only for logged-in visitors when owner allows */}
                    {showContactButton && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setWishlistContactItem(item);
                        }}
                        className="mt-1 w-full flex items-center justify-center gap-1 px-2 py-1 btn-primary text-[10px] font-bold rounded transition-default font-body"
                        title="Let them know you have this item!"
                      >
                        <Gift className="icon-xs" />
                        I Have This!
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && profile && (
        <EditProfileModal
          currentProfile={{
            displayName: profile.displayName || profile.username || '',
            bio: profile.bio || '',
            avatarUrl: profile.avatarUrl || '',
            bannerUrl: profile.bannerUrl || '',
            socialLinks: profile.socialLinks || {}
          }}
          onClose={() => setShowEditModal(false)}
          onSave={(updatedProfile) => {
            // Update local profile state with new data
            setProfile(prev => prev ? {
              ...prev,
              displayName: updatedProfile.displayName,
              bio: updatedProfile.bio,
              avatarUrl: updatedProfile.avatarUrl,
              bannerUrl: updatedProfile.bannerUrl,
              socialLinks: updatedProfile.socialLinks
            } : null);
            setShowEditModal(false);
          }}
        />
      )}

      {/* Item Detail Modals - Open directly on profile without navigation */}
      {selectedItem && selectedItem.itemType === 'polish' && (
        <PolishDetailModal
          polish={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {selectedItem && selectedItem.itemType === 'plate' && (
        <PlateDetailModal
          plate={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {selectedItem && selectedItem.itemType === 'transfer' && (
        <DecalDetailModal
          decal={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {selectedItem && selectedItem.itemType === 'embellishment' && (
        <EmbellishmentDetailModal
          embellishment={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {selectedItem && selectedItem.itemType === 'tool' && (
        <ToolDetailModal
          tool={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {selectedItem && selectedItem.itemType === 'powder' && (
        <BuffingPowderDetailModal
          powder={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {selectedItem && selectedItem.itemType === 'acrylic' && (
        <AcrylicDetailModal
          acrylic={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={showAsOwner ? handleSaveItem : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setItems(prev => prev.filter(item => item.id !== deletedId));
            setSelectedItem(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}

      {/* Wishlist Contact Modal - for contacting profile owner about their wishlist items */}
      {wishlistContactItem && userId && profile && (
        <WishlistContactModal
          isOpen={!!wishlistContactItem}
          onClose={() => setWishlistContactItem(null)}
          currentUserId={getCurrentUser()?.uid || ''}
          wisherId={userId}
          wisherUsername={profile.username}
          wisherDisplayName={profile.displayName}
          item={{
            id: wishlistContactItem.id,
            name: wishlistContactItem.name,
            brand: wishlistContactItem.brand,
            isoPriority: wishlistContactItem.isoPriority,
            photos: wishlistContactItem.photos,
            photoUrl: wishlistContactItem.photoUrl
          }}
        />
      )}

      {/* Creation Detail Modal */}
      {selectedCreation && userId && (
        <CreationDetailModal
          creation={selectedCreation}
          onClose={() => setSelectedCreation(null)}
          onUpdate={showAsOwner ? () => {
            // Refresh creations after update
            setSelectedCreation(null);
          } : undefined}
          onDelete={showAsOwner ? (deletedId) => {
            setCreations(prev => prev.filter(c => c.id !== deletedId));
            setSelectedCreation(null);
          } : undefined}
          isPublicView={!showAsOwner}
        />
      )}
    </div>
  );
}
